import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { ReactComponent as EnvelopeSVG } from "go-core/images/svg/message.svg";
import { ReactComponent as TelephoneSVG } from "go-core/images/svg/phone.svg";
import { ReactComponent as AvatarSVG } from "../../../../../../images/svg/organization/avatar.svg";
import { AdminOrganizationUserApi } from "../../../../../../services/Api/Admin/adminTypes";
import { OrganizationApplicationApi } from "../../../../../../services/Api/types";
import { RenderApplicationLogo } from "../../../RenderApplicationLogo/RenderApplicationLogo";
import { OrganizationPreviewRequests } from "../services/types";

interface Props {
	user: AdminOrganizationUserApi;
	applications: OrganizationApplicationApi[];
	requests: OrganizationPreviewRequests;
	organizationId: number;
}

const OrganizationUserCard = ({ user, applications, requests, organizationId }: Props) => {
	const { t } = useTranslation("admin");
	const blackTint = { color: "black" };
	const [loading, setLoading] = useState<boolean>(false);
	const { addFlash, addSuccessFlash } = useFlash();

	const drawApplications = () => {
		return applications.map((app, index) => {
			const existingApplication = user.applications?.find(
				(application) => application.application_type === app.slug
			);

			return (
				<div
					key={`user-${user.id}-app-${index}`}
					className={`d-flex justify-content-between align-items-center ${
						index !== applications.length - 1 && "mb-2"
					}`}
				>
					<div className="d-flex align-items-center">
						<span
							className={`${!existingApplication ? "application-img-disabled" : ""}`}
							style={!existingApplication ? { filter: "grayscale(100%)" } : undefined}
						>
							<RenderApplicationLogo slug={app.name} />
						</span>
						<span
							className={`ml-2 ${!existingApplication && "text-muted"}`}
							style={existingApplication && blackTint}
						>
							{app.name}
						</span>
					</div>
					<span
						className={`${!existingApplication && "text-muted"}`}
						style={existingApplication && blackTint}
					>
						{existingApplication ? existingApplication.role_name : "~"}
					</span>
				</div>
			);
		});
	};

	const resendInvitation = async (user: AdminOrganizationUserApi) => {
		if (!requests.resendInvitation) return;
		setLoading(true);
		try {
			await requests.resendInvitation(organizationId, user.id);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};
	const getUserPermissions = () => {
		return user?.permissions?.map((permission) => t(`enums.users.permissions.${permission}`))?.join(", ");
	};

	return (
		<Card className="organization-user-card h-100 mb-0">
			<Card.Body className={"pb-0"}>
				<div className="d-flex flex-column">
					<div className="d-flex">
						{user.avatar_link ? (
							<img className="user-avatar" alt="User avatar" src={user.avatar_link.default_link} />
						) : (
							<AvatarSVG className="user-avatar" />
						)}
						<div className="d-flex flex-column ms-3 w-100">
							<h5 className="mb-0">{user.name}</h5>
							<small className="text-muted font-weight-bold permissions-box" title={getUserPermissions()}>
								{getUserPermissions()}
							</small>
						</div>
					</div>
					<div className="mt-3">
						<div className="card-body-item flex-wrap">
							<EnvelopeSVG />
							<span
								className={`${!user.email && "text-muted"}`}
								style={user.email ? blackTint : undefined}
							>
								{user.email ?? "~"}
							</span>
						</div>
						<div className="card-body-item">
							<TelephoneSVG />
							<span
								className={`${!user.phone_number && "text-muted"}`}
								style={user.phone_number ? blackTint : undefined}
							>
								{user.phone_number ? `+${user.phone_number}` : "~"}
							</span>
						</div>
					</div>
					<hr className="w-100" />
					<div className="d-flex flex-column">{drawApplications()}</div>
					{applications.length > 0 && <hr className="w-100" />}
					{user.type === "INVITE" && (
						<div className={"alert alert-warning"}>
							<p>Użytkownik nie zaakceptował zaproszenia</p>
							<ButtonLoading
								loading={loading}
								onClick={() => resendInvitation(user)}
								className={"btn btn-default w-100 btn-sm"}
							>
								Wyślij zaproszenie jeszcze raz
							</ButtonLoading>
						</div>
					)}
					{/*<div className="d-flex flex-column">*/}
					{/*	<div className="d-flex justify-content-between align-items-center flex-wrap">*/}
					{/*		<div className="card-body-item mb-2">*/}
					{/*			<div className="d-flex align-items-center">*/}
					{/*				<CalendarSVG />*/}
					{/*				<small className="font-weight-bold text-muted">*/}
					{/*					{t("modules.organization.field.register_date.title")}*/}
					{/*				</small>*/}
					{/*			</div>*/}
					{/*		</div>*/}
					{/*		<span className="ms-auto text-muted">~</span>*/}
					{/*	</div>*/}
					{/*	<div className="d-flex justify-content-between align-items-center flex-wrap">*/}
					{/*		<div className="card-body-item mb-2">*/}
					{/*			<div className="d-flex align-items-center">*/}
					{/*				<ClockSVG />*/}
					{/*				<small className="font-weight-bold text-muted">*/}
					{/*					{t("modules.organization.field.last_seen.title")}*/}
					{/*				</small>*/}
					{/*			</div>*/}
					{/*		</div>*/}
					{/*		<span className="ms-auto text-muted">~</span>*/}
					{/*	</div>*/}
					{/*	<div className="d-flex justify-content-between align-items-center flex-wrap">*/}
					{/*		<div className="card-body-item mb-0">*/}
					{/*			<div className="d-flex align-items-center">*/}
					{/*				<ChatSVG />*/}
					{/*				<small className="font-weight-bold text-muted">*/}
					{/*					{t("modules.organization.field.last_contact.title")}*/}
					{/*				</small>*/}
					{/*			</div>*/}
					{/*		</div>*/}
					{/*		<span className="ms-auto text-muted">~</span>*/}
					{/*	</div>*/}
					{/*</div>*/}
				</div>
			</Card.Body>
		</Card>
	);
};

export default OrganizationUserCard;

// t("enums.users.permissions.DEFAULT");
// t("enums.users.permissions.USERS_MANAGMENT");
